'use client';

import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLineSubmit2 } from '@/app/components/icons/BaseLineSubmit2';
import { Loading } from '@/app/components/icons/Loading';
import { getAudioDisplayName, UploadedAudioCard } from '@/app/components/shared/UploadedAudioCard';
import { toast } from '@/app/components/ui/use-toast';
import { useAudioPlay, useAudioUpload } from '@/app/hooks';
import type { AudioUploadFile, FormAudioPrompt } from '@/app/types';
import { cn } from '@/app/utils';
import { ToolMediaUploadHeader } from './ToolMediaUploadHeader';

interface ToolAudioUploadProps {
  audioPrompt?: FormAudioPrompt | null;
  onAudioUpload: (file: AudioUploadFile) => void;
  onRemoveAudio?: () => void;
  label?: string;
  helperText?: string;
  className?: string;
  allowRemove?: boolean;
  maxDuration: number;
  minDuration: number;
  maxSize: number;
  /**
   * 传给原生文件选择的 accept，并与校验一致（传入非空时，仅允许匹配该规则的扩展名/MIME）。
   * 不传则使用内置默认（避免系统把视频 .mp4 当音频可选）。
   * 若业务需要接受视频容器中的音轨，可显式传入如 `.mp4,video/mp4,audio/mp4`。
   */
  accept?: string;
}

interface AudioUploadPlaybackCardProps {
  audioSource: string;
  audioId: string;
  audioName: string;
  allowRemove: boolean;
  onSelectAudio: () => void;
  onRemove?: () => void;
}

const AudioUploadPlaybackCard: FC<AudioUploadPlaybackCardProps> = ({
  audioSource,
  audioId,
  audioName,
  allowRemove,
  onSelectAudio,
  onRemove,
}) => {
  const { isPlaying, handlePlayPause } = useAudioPlay({
    url: audioSource,
    id: audioId,
  });

  return (
    <UploadedAudioCard
      audioName={audioName}
      isPlaying={isPlaying}
      playDisabled={!audioSource}
      onSelectAudio={onSelectAudio}
      onPlayPause={handlePlayPause}
      onRemove={onRemove}
      showRemoveButton={allowRemove}
    />
  );
};

export const ToolAudioUpload: FC<ToolAudioUploadProps> = ({
  audioPrompt,
  onAudioUpload,
  onRemoveAudio,
  label,
  helperText,
  className,
  allowRemove = true,
  maxDuration,
  minDuration,
  maxSize,
  accept,
}) => {
  const t = useTranslations();
  const [isClient, setIsClient] = useState(false);
  const { uploading, selectAudio } = useAudioUpload({
    maxDuration,
    minDuration,
    maxSize,
    accept,
    allowTempMode: true,
    onChange: (file) => {
      onAudioUpload(file);

      if (file.status === 'done' && file.file) {
        toast({
          message: t('sharedComponent.audioUploader.success.uploadSuccess'),
          type: 'success',
        });
      }
    },
  });

  useEffect(() => {
    setIsClient(true);
  }, []);

  const audioSource = audioPrompt?.prompt?.preview || audioPrompt?.prompt?.src || '';
  const audioName = getAudioDisplayName(
    audioPrompt?.prompt?.src || audioPrompt?.prompt?.preview,
    audioPrompt?.prompt?._tempFile?.name || audioPrompt?.prompt?.name,
  );
  const isUploading = uploading || audioPrompt?.status === 'uploading';
  const hasAudio = audioPrompt?.status === 'done' && !!audioPrompt.prompt;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <ToolMediaUploadHeader icon={<BaseLineMusic className="size-5 text-system-white" />} title={label} />

      <div
        className={cn(
          'relative w-full border border-system-white08 bg-system-white04 backdrop-blur-[30px]',
          hasAudio ? 'rounded-12' : 'flex min-h-[64px] flex-col justify-center rounded-16 px-4 py-4',
        )}
      >
        {isUploading ? (
          <div className="flex flex-col items-center justify-center gap-2 text-system-white/60">
            <Loading className="animate-spin text-2xl text-system-white" />
            <span className="text-xs leading-[1.4]">{t('create.createForm.upload.uploading')}</span>
          </div>
        ) : hasAudio && audioPrompt?.prompt ? (
          isClient ? (
            <AudioUploadPlaybackCard
              audioSource={audioSource}
              audioId={audioPrompt.id || 'tool-audio-upload'}
              audioName={audioName}
              allowRemove={allowRemove}
              onSelectAudio={selectAudio}
              onRemove={onRemoveAudio}
            />
          ) : (
            <UploadedAudioCard
              audioName={audioName}
              playDisabled
              onSelectAudio={selectAudio}
              onRemove={onRemoveAudio}
              showRemoveButton={allowRemove}
            />
          )
        ) : (
          <button
            type="button"
            className="group flex h-full w-full cursor-pointer items-center justify-center gap-2"
            onClick={selectAudio}
          >
            <BaseLineSubmit2 className="size-4 text-system-white" />
            <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
              {t('tools.uploadNewFile')}
            </span>
          </button>
        )}
      </div>

      {helperText ? <div className="text-system-white/40 text-xs leading-[1.4]">{helperText}</div> : null}
    </div>
  );
};
